import { Box, Container, Typography, Button, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Logout } from "@mui/icons-material";
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import TaskCard from "../molecules/TaskCard";
import { useTheme } from "../../ThemeContext";
import { uiManagementList, developerList } from "../../utils";

interface TaskSelectorParams {
}

const TaskSelector = ({
}: TaskSelectorParams) => {
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();
    const { mode, toggleColorMode } = useTheme();

    const canAccessUIManagement = accounts[0] && uiManagementList.includes(accounts[0].username.toLowerCase());
    const canAccessWebhookDebugger = accounts[0] && developerList.includes(accounts[0].username.toLowerCase());

    const handleTranscriptClick = () => {
        navigate('/review');
    };

    const handleTaxReturnClick = () => {
        navigate('/returns');
    };

    const handleUIManagementClick = () => {
        navigate('/ui-management');
    };

    const handleWebhookDebuggerClick = () => {
        navigate('/webhook-debugger');
    };

    const handleSignOut = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: "/",
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4, minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Theme Toggle Button */}
            <IconButton
                sx={{ position: 'absolute', top: 16, right: 16 }}
                onClick={toggleColorMode}
                color="inherit"
            >
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            {/* Logo Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Box
                    component="img"
                    sx={{
                        height: 80,
                        maxWidth: 300,
                    }}
                    alt="Halcyon Still Water"
                    src={mode === 'dark' ? "/logo-white.png" : "/logo.png"}
                />
            </Box>

            {/* Title Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                    Operations Commander
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Select a processing task to begin
                </Typography>
            </Box>

            {/* Cards Section */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Grid container spacing={3} sx={{ maxWidth: 800 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            onClick={handleTranscriptClick}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .MuiCard-root': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3,
                                    },
                                },
                            }}
                        >
                            <TaskCard
                                title="Transcript Processing"
                                description="Review Form 8821 and Form 1177 submissions for verification"
                                taskCount={-1} // You can replace with actual count from Redux/API
                                icon="DescriptionOutlined"
                                color="#2196f3"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            onClick={handleTaxReturnClick}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    '& .MuiCard-root': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3,
                                    },
                                },
                            }}
                        >
                            <TaskCard
                                title="Tax Return Processing"
                                description="Process and organize tax returns"
                                taskCount={-1} // You can replace with actual count from Redux/API
                                icon="Assignment"
                                color="#4caf50"
                            />
                        </Box>
                    </Grid>
                    {canAccessUIManagement && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                onClick={handleUIManagementClick}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        '& .MuiCard-root': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 3,
                                        },
                                    },
                                }}
                            >
                                <TaskCard
                                    title="UI Management"
                                    description="Manage turn times and UI alerts"
                                    taskCount={-1}
                                    icon="Settings"
                                    color="#ff9800"
                                />
                            </Box>
                        </Grid>
                    )}
                    {canAccessWebhookDebugger && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                onClick={handleWebhookDebuggerClick}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        '& .MuiCard-root': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 3,
                                        },
                                    },
                                }}
                            >
                                <TaskCard
                                    title="Webhook Debugger"
                                    description="View and manage captured webhooks"
                                    taskCount={-1}
                                    icon="BugReport"
                                    color="#9c27b0"
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Sign Out Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, pb: 2 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<Logout />}
                    onClick={handleSignOut}
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontSize: '1rem',
                        '&:hover': {
                            backgroundColor: 'primary.light',
                            borderColor: 'primary.dark',
                        },
                    }}
                >
                    Sign Out
                </Button>
            </Box>
        </Container>
    );
};

export default TaskSelector;
